import { ErrorPageHeader } from 'components/header/ErrorPageHeader';
import { useRouteError } from 'react-router';
import styles from './ReactRouterErrorBoundary.module.scss';
import ErrorPlaceholder from 'assets/img/error-placeholder.svg?react';
import { Button } from 'components/uikit/buttons/Button';
import { useTranslation } from 'react-i18next';
import { errorToString, NetworkError } from 'helpers/error-helpers';
import { isAxiosError } from 'axios';
import { QueryFactory } from 'services/api';
import { ApiException, ProblemDetails } from 'services/api/api-client.types';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Links } from 'application/constants/links';
import { useMemo } from 'react';
import { AppLink } from 'components/uikit/buttons/AppLink';

/*
 * This is an ErrorBoundary that should be provided to `createBrowserRouter`.
 * It just rethrows an error so that it will be cought by `QuerySuspenseErrorWrapper`.
 * We could include all `QuerySuspenseErrorWrapper` error-handling logic into `ReactRouterErrorBoundary`,
 * but we'd still need to wrap all routes in <Suspense> and <QueryErrorResetBoundary>.
 */
export const ReactRouterErrorBoundary = () => {
  const error = useRouteError() as Error;
  const i18n = useTranslation();

  const errorString = errorToString(error);

  const versionQuery = QueryFactory.VersionQuery.useVersionQuery({
    throwOnError: false,
  });
  const isServerAvailable = !!versionQuery.data;

  // if error is NetworkError, but server is available, there's a high chance we are trying to request an old chunk of JS or CSS
  // (unfortunately it's impossible to get the error code of Network response and compare it with 404).
  // so we show proper error message and offer to reload the whole page
  const isServerUpdated =
    errorString === NetworkError && !isAxiosError(error) && isServerAvailable;

  let statusCode: number | undefined;
  let traceId: string | undefined;

  if (isAxiosError(error)) {
    traceId = error.traceId;
  } else if (error instanceof ApiException) {
    traceId = (error.response as unknown as ProblemDetails).traceId;
    statusCode =
      (error.response as unknown as ProblemDetails).status ?? undefined;
  }

  const buttons = useErrorButtons();

  return (
    <div className={styles.wrapper}>
      <ErrorPageHeader />
      <div className={styles.container}>
        <ErrorPlaceholder />
        <div className={styles.error}>
          <div className={styles.errorInfo}>
            {statusCode && (
              <span className={styles.statusCode}>{statusCode}</span>
            )}
            <span className={styles.message}>
              {isServerUpdated
                ? i18n.t('suspense.server_updated')
                : errorString}
            </span>
          </div>
          <div className={styles.errorActions}>{buttons}</div>
        </div>
      </div>
      {traceId && (
        <span className={styles.traceId}>
          {i18n.t('suspense.trace_id', { traceId })}
        </span>
      )}
    </div>
  );
};

enum ErrorButtonType {
  Reload = 'reload',
  GoToProjects = 'go_to_projects',
  GoToRevision = 'go_to_revision',
  GoToTemplates = 'go_to_templates',
}

const useErrorButtons = () => {
  const i18n = useScopedTranslation(
    'Component.ReactRouterErrorBoundary.Buttons',
  );
  let buttonType: ErrorButtonType | undefined;

  const reloadMatch =
    Links.Authorized.Dashboard.useMatch() ||
    Links.Authorized.Projects.useMatch();
  if (reloadMatch) buttonType = ErrorButtonType.Reload;

  const goToProjectsMatch =
    Links.Authorized.Templates.useMatch() ||
    Links.Authorized.TemplatesMerging.useMatch() ||
    Links.Authorized.ProjectDetails.useMatch() ||
    Links.Authorized.CreateRevision.useMatch();
  if (goToProjectsMatch) buttonType = ErrorButtonType.GoToProjects;

  const goToRevisionMatch =
    Links.Authorized.CreateDocument.useMatch() ||
    Links.Authorized.OpenDocument.useMatch();
  if (goToRevisionMatch) buttonType = ErrorButtonType.GoToRevision;

  const goToTemplatesMatch = Links.Authorized.OpenTemplate.useMatch();
  if (goToTemplatesMatch) buttonType = ErrorButtonType.GoToTemplates;

  const button: React.ReactNode = useMemo(() => {
    let buttonProps: {
      linkTo?: string;
      buttonTitle: string;
      onClick?: () => void;
    };

    switch (buttonType) {
      case ErrorButtonType.GoToProjects:
        buttonProps = {
          linkTo: Links.Authorized.Projects.link(),
          buttonTitle: i18n.t(ErrorButtonType.GoToProjects),
        };
        break;
      case ErrorButtonType.GoToRevision:
        buttonProps = {
          linkTo: Links.Authorized.ProjectDetails.link(
            {
              projectId: goToRevisionMatch!.params.projectId,
            },
            {
              releaseId: goToRevisionMatch!.params.releaseId,
            },
          ),
          buttonTitle: i18n.t(ErrorButtonType.GoToRevision),
        };
        break;
      case ErrorButtonType.GoToTemplates:
        buttonProps = {
          linkTo: Links.Authorized.Templates.link(),
          buttonTitle: i18n.t(ErrorButtonType.GoToTemplates),
        };
        break;
      case ErrorButtonType.Reload:
      default:
        buttonProps = {
          buttonTitle: i18n.t(ErrorButtonType.Reload),
          onClick: async () => {
            document.location.reload();
          },
        };
        break;
    }

    return (
      <AppLink to={buttonProps.linkTo}>
        <Button
          onClick={buttonProps.onClick}
          className={styles.button}
          title={buttonProps.buttonTitle}
        />
      </AppLink>
    );
  }, [buttonType]);

  return (
    <>
      {button}

      <Button
        onClick={() => history.back()}
        className={styles.button}
        title={i18n.t('go_back')}
      />
    </>
  );
};
